import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Lock, Mail, Loader2, Eye, EyeOff } from 'lucide-react';

const LOGIN_BG_IMAGE = "https://customer-assets.emergentagent.com/job_sports-platform-8/artifacts/tpwdnjrf_ChatGPT%20Image%2027%20de%20abr.%20de%202026%2C%2015_42_00.png";

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(formatApiError(err));
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left - Image with zoom animation */}
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden">
        <div
          className="absolute inset-0 animate-login-zoom"
          style={{
            backgroundImage: `url(${LOGIN_BG_IMAGE})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/80 via-[#0A0A0A]/40 to-[#0A0A0A]/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/90 via-transparent to-[#0A0A0A]/30" />

        {/* Branding */}
        <div className="relative z-10 flex flex-col justify-end h-full p-12">
          <h1 className="text-5xl font-black tracking-tighter font-['Barlow_Condensed'] text-white uppercase">
            Sport<span className="text-[#007AFF]">Manager</span>
          </h1>
          <p className="mt-3 text-lg text-zinc-300 max-w-md">
            Plataforma completa de gestao esportiva. Controle alunos, turmas, treinos e financeiro.
          </p>
          <div className="mt-5 flex items-center gap-4 flex-wrap">
            <span className="flex items-center gap-1.5 text-xs text-zinc-400 uppercase tracking-wider">
              <span className="w-1.5 h-1.5 rounded-full bg-[#007AFF]" />Volei
            </span>
            <span className="flex items-center gap-1.5 text-xs text-zinc-400 uppercase tracking-wider">
              <span className="w-1.5 h-1.5 rounded-full bg-[#007AFF]" />Futebol
            </span>
            <span className="flex items-center gap-1.5 text-xs text-zinc-400 uppercase tracking-wider">
              <span className="w-1.5 h-1.5 rounded-full bg-[#007AFF]" />Basquete
            </span>
            <span className="flex items-center gap-1.5 text-xs text-zinc-400 uppercase tracking-wider">
              <span className="w-1.5 h-1.5 rounded-full bg-[#007AFF]" />Handebol
            </span>
            <span className="flex items-center gap-1.5 text-xs text-zinc-400 uppercase tracking-wider">
              <span className="w-1.5 h-1.5 rounded-full bg-[#007AFF]" />Lutas
            </span>
          </div>
        </div>
      </div>

      {/* Right - Form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-[#0A0A0A] relative overflow-hidden">
        {/* Mobile background image */}
        <div
          className="lg:hidden absolute inset-0 animate-login-zoom opacity-20"
          style={{
            backgroundImage: `url(${LOGIN_BG_IMAGE})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="lg:hidden absolute inset-0 bg-[#0A0A0A]/85" />

        <Card className="w-full max-w-md bg-[#141414] border-[#27272A] relative z-10">
          <CardHeader className="text-center pb-2">
            <div className="lg:hidden mb-4">
              <h1 className="text-3xl font-black tracking-tighter font-['Barlow_Condensed'] text-white uppercase">
                Sport<span className="text-[#007AFF]">Manager</span>
              </h1>
            </div>
            <CardTitle className="text-2xl font-bold font-['Barlow_Condensed'] tracking-tight text-white">
              ACESSAR PLATAFORMA
            </CardTitle>
            <CardDescription className="text-zinc-400">
              Entre com suas credenciais para continuar
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="login-error-message">
                  {error}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-zinc-300 text-xs tracking-[0.15em] uppercase font-bold">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    id="email"
                    type="email"
                    data-testid="login-email-input"
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-[#0A0A0A] border-[#27272A] text-white placeholder:text-zinc-600 focus:border-[#007AFF] focus:ring-[#007AFF]"
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-zinc-300 text-xs tracking-[0.15em] uppercase font-bold">Senha</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    data-testid="login-password-input"
                    placeholder="********"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 bg-[#0A0A0A] border-[#27272A] text-white placeholder:text-zinc-600 focus:border-[#007AFF] focus:ring-[#007AFF]"
                    required
                  />
                  <button
                    type="button"
                    data-testid="toggle-password-visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                data-testid="login-submit-button"
                disabled={loading}
                className="w-full bg-[#007AFF] hover:bg-[#0066DD] text-white font-bold h-11 text-sm tracking-wide uppercase transition-all duration-200 active:scale-[0.98]"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Entrar'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
